import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Video, Square, Send, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth, API } from "@/App";
import Header from "@/components/Header";

// Real Duet recorder: side-by-side compositing of reference video + user webcam
// All client-side via Canvas + MediaRecorder. Final blob uploaded to backend.
export default function DuetRecorder() {
  const navigate = useNavigate();
  const { videoId } = useParams();
  const { user } = useAuth();

  const [refVideo, setRefVideo] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [caption, setCaption] = useState("");

  const refVideoElRef = useRef(null);
  const userVideoElRef = useRef(null);
  const canvasRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const userStreamRef = useRef(null);
  const animationRef = useRef(null);

  // Fetch reference video
  const loadRefVideo = useCallback(async () => {
    const r = await fetch(`${API}/videos/${videoId}`, { credentials: "include" });
    if (r.ok) setRefVideo(await r.json());
  }, [videoId]);

  useEffect(() => { loadRefVideo(); }, [loadRefVideo]);

  // Initialize user webcam preview
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ video: { width: 480, height: 720 }, audio: true });
        if (!mounted) { stream.getTracks().forEach(t => t.stop()); return; }
        userStreamRef.current = stream;
        if (userVideoElRef.current) userVideoElRef.current.srcObject = stream;
      } catch (e) {
        toast.error("Camera/mic permission needed for duet");
      }
    })();
    return () => {
      mounted = false;
      if (userStreamRef.current) userStreamRef.current.getTracks().forEach(t => t.stop());
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, []);

  // Canvas compositing draw loop (called once recording starts)
  const startCompositingLoop = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const W = canvas.width, H = canvas.height;
    const draw = () => {
      ctx.fillStyle = "#000";
      ctx.fillRect(0, 0, W, H);
      const refEl = refVideoElRef.current;
      const userEl = userVideoElRef.current;
      // Left: reference video
      if (refEl && refEl.readyState >= 2) {
        ctx.drawImage(refEl, 0, 0, W / 2, H);
      }
      // Right: user webcam
      if (userEl && userEl.readyState >= 2) {
        ctx.drawImage(userEl, W / 2, 0, W / 2, H);
      }
      // Center divider
      ctx.fillStyle = "#FF00FF";
      ctx.fillRect(W / 2 - 1, 0, 2, H);
      animationRef.current = requestAnimationFrame(draw);
    };
    draw();
  };

  const startRecording = async () => {
    if (!refVideoElRef.current || !userStreamRef.current) {
      toast.error("Videos not ready yet");
      return;
    }
    setRecordedBlob(null);
    chunksRef.current = [];

    // Start ref video playback from beginning
    refVideoElRef.current.currentTime = 0;
    await refVideoElRef.current.play();

    // Start canvas compositing loop
    startCompositingLoop();

    // Capture canvas as a video stream + audio from user mic
    const canvasStream = canvasRef.current.captureStream(30);
    const userAudioTrack = userStreamRef.current.getAudioTracks()[0];
    if (userAudioTrack) canvasStream.addTrack(userAudioTrack);

    const mr = new MediaRecorder(canvasStream, { mimeType: "video/webm;codecs=vp8,opus" });
    mediaRecorderRef.current = mr;
    mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    mr.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      setRecordedBlob(blob);
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
      if (refVideoElRef.current) refVideoElRef.current.pause();
    };
    mr.start();
    setRecording(true);

    // Auto-stop when reference video ends
    refVideoElRef.current.onended = () => {
      if (mr.state === "recording") mr.stop();
      setRecording(false);
    };
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === "recording") {
      mediaRecorderRef.current.stop();
    }
    setRecording(false);
  };

  const uploadDuet = async () => {
    if (!recordedBlob) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([recordedBlob], `duet_${Date.now()}.webm`, { type: "video/webm" }));
      const upRes = await fetch(`${API}/upload/video`, { method: "POST", credentials: "include", body: fd });
      if (!upRes.ok) throw new Error("Upload failed");
      const { url: videoUrl } = await upRes.json();

      // Create duet record (backend stores reference + user duet URL)
      const dRes = await fetch(`${API}/duets`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          original_type: "video",
          original_id: videoId,
          duet_media_url: videoUrl,
          title: caption || `Duet with ${refVideo?.user_name || ""}`,
        }),
      });
      if (!dRes.ok) throw new Error("Duet save failed");
      toast.success("Duet posted!");
      navigate("/videos");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "linear-gradient(180deg, #001a33 0%, #000022 100%)" }}>
      <Header />
      <main className="flex-1 container mx-auto px-4 py-4 max-w-3xl">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[#FF66FF] mb-4" data-testid="duet-back-btn">
          <ArrowLeft className="w-5 h-5" /> Back
        </button>

        <h1 className="text-2xl font-bold text-[#FF00FF] mb-2" style={{ textShadow: "0 0 10px #FF00FF" }} data-testid="duet-title">Record a Duet</h1>
        <p className="text-sm text-[#99CCFF] mb-4">Side-by-side: reference video on the left, you on the right.</p>

        {/* Hidden source elements */}
        <video ref={refVideoElRef} src={refVideo?.video_url} crossOrigin="anonymous" className="hidden" playsInline />
        <video ref={userVideoElRef} className="hidden" autoPlay muted playsInline />

        {/* Live preview canvas (640x720 → 16:18 portrait-ish duet) */}
        <canvas ref={canvasRef} width={640} height={720} className="w-full bg-black border-2 border-[#FF00FF]" data-testid="duet-canvas" />

        {/* Caption */}
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Add a caption..."
          className="w-full mt-3 bg-[rgba(0,0,0,0.4)] border-2 border-[#6600CC] text-[#00FFFF] px-3 py-2 outline-none focus:border-[#FF00FF]"
          data-testid="duet-caption-input"
        />

        {/* Controls */}
        <div className="flex gap-2 mt-3">
          {!recording && !recordedBlob && (
            <button onClick={startRecording} className="myspace-btn-primary flex-1 inline-flex items-center justify-center gap-2 py-3" data-testid="duet-record-btn">
              <Video className="w-5 h-5" /> Start Recording
            </button>
          )}
          {recording && (
            <button onClick={stopRecording} className="myspace-btn-primary flex-1 inline-flex items-center justify-center gap-2 py-3 bg-[#FF0000] animate-pulse" data-testid="duet-stop-btn">
              <Square className="w-5 h-5" /> Stop
            </button>
          )}
          {recordedBlob && !recording && (
            <>
              <button onClick={() => { setRecordedBlob(null); chunksRef.current = []; }} className="myspace-btn flex-1" data-testid="duet-redo-btn">
                Redo
              </button>
              <button onClick={uploadDuet} disabled={uploading} className="myspace-btn-primary flex-1 inline-flex items-center justify-center gap-2 py-3 disabled:opacity-50" data-testid="duet-post-btn">
                <Send className="w-5 h-5" /> {uploading ? "Uploading..." : "Post Duet"}
              </button>
            </>
          )}
        </div>

        {recordedBlob && (
          <div className="mt-4">
            <p className="text-sm text-[#00FFFF] mb-1">Preview</p>
            <video src={URL.createObjectURL(recordedBlob)} controls className="w-full bg-black border-2 border-[#FF00FF]" />
          </div>
        )}
      </main>
    </div>
  );
}
